'use client';

import { useEffect, useRef } from 'react';
import { cn } from '@utils/cn';

// Fixed per-bar weights (0.4–1.0, varied) so the bars don't move in lockstep.
const BARS = Array.from({ length: 24 }, (_, i) => ({
  id: `bar-${i}`,
  weight: 0.4 + 0.6 * Math.abs(Math.sin(i * 2.7 + 1)),
}));

interface VoiceWaveformProps {
  /** Current mic level, 0..1 (read every animation frame — must be a stable ref-reader). */
  getLevel: () => number;
  className?: string;
}

/**
 * @description Amplitude-driven waveform shown in the input's placeholder spot while
 * the microphone is listening. Bar heights are written directly to the DOM each frame
 * so the animation never re-renders React.
 */
export const VoiceWaveform = ({ getLevel, className }: VoiceWaveformProps) => {
  const barsRef = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    let raf = requestAnimationFrame(function tick() {
      const level = getLevel();
      barsRef.current.forEach((bar, i) => {
        if (bar) bar.style.height = `${15 + 85 * level * BARS[i].weight}%`;
      });
      raf = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(raf);
  }, [getLevel]);

  return (
    <div aria-hidden className={cn('flex h-5 w-full items-center justify-evenly', className)}>
      {BARS.map((bar, i) => (
        <span
          key={bar.id}
          ref={(el) => { barsRef.current[i] = el; }}
          className="w-0.5 rounded-full bg-secondary"
          style={{ height: '15%' }}
        />
      ))}
    </div>
  );
};
